import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";

import { Icon } from "../../components/Icon";

import {
  buildVoiceFileName,
  downsampleWaveform,
  formatVoiceTime,
  pickVoiceMimeType,
  ratioFromPointer,
} from "./chatUtils";
import { voicePlayback } from "./voicePlaybackController";

const MAX_DURATION_MS = 60_000;
const MIN_DURATION_MS = 1_000;
const SAMPLE_INTERVAL_MS = 100;
const WAVEFORM_BARS = 64;

interface VoiceRecorderProps {
  disabled?: boolean;
  onCancel: () => void;
  onSend: (file: File, durationMs: number, waveform: number[]) => void;
}

type Phase = "preview" | "recording";

export default function VoiceRecorder({
  disabled,
  onCancel,
  onSend,
}: VoiceRecorderProps) {
  const [phase, setPhase] = useState<Phase>("recording");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [liveSamples, setLiveSamples] = useState<number[]>([]);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState(0);
  const [dragProgress, setDragProgress] = useState<number | null>(null);
  const [isConverting, setIsConverting] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const sampleIntervalRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const stoppedAtRef = useRef<number>(0);
  const liveSamplesRef = useRef<number[]>([]);
  const stopIntentRef = useRef<"preview" | "send">("preview");
  const sendingRef = useRef(false);
  const mimeInfoRef = useRef<{ extension: string; mimeType: string }>({
    extension: "webm",
    mimeType: "",
  });

  const finalBlobRef = useRef<Blob | null>(null);
  const finalUrlRef = useRef<string | null>(null);
  const finalSamplesRef = useRef<number[]>([]);
  const finalDurationRef = useRef<number>(0);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const waveformContainerRef = useRef<HTMLDivElement | null>(null);
  const [barSlots, setBarSlots] = useState(60);

  const clearIntervals = () => {
    if (sampleIntervalRef.current !== null) {
      window.clearInterval(sampleIntervalRef.current);
      sampleIntervalRef.current = null;
    }
    if (timerIntervalRef.current !== null) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const releaseMediaResources = () => {
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch {
        // noop
      }
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch {
        // noop
      }
      analyserRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => undefined);
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const cleanupPreview = () => {
    const audio = previewAudioRef.current;
    if (audio) {
      audio.pause();
      audio.src = "";
      voicePlayback.release(audio);
      previewAudioRef.current = null;
    }
    if (finalUrlRef.current) {
      URL.revokeObjectURL(finalUrlRef.current);
      finalUrlRef.current = null;
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    stoppedAtRef.current = performance.now();
    clearIntervals();
    try {
      recorder.stop();
    } catch {
      releaseMediaResources();
      onCancel();
    }
  };

  const handleCancel = () => {
    clearIntervals();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.onstop = null;
        recorder.stop();
      } catch {
        // noop
      }
    }
    recorderRef.current = null;
    releaseMediaResources();
    cleanupPreview();
    onCancel();
  };

  const startRecording = async () => {
    try {
      voicePlayback.stopAll();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      streamRef.current = stream;

      const mimeInfo = pickVoiceMimeType();
      mimeInfoRef.current = mimeInfo;
      const recorder = mimeInfo.mimeType
        ? new MediaRecorder(stream, { mimeType: mimeInfo.mimeType })
        : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const actualMime =
          recorder.mimeType || mimeInfoRef.current.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: actualMime });
        const durationMs = stoppedAtRef.current - startedAtRef.current;
        finalSamplesRef.current = liveSamplesRef.current.slice();
        finalDurationRef.current = durationMs;
        releaseMediaResources();
        voicePlayback.setRecording(false);
        if (durationMs < MIN_DURATION_MS) {
          toast.error("Recording too short");
          onCancel();
          return;
        }
        finalBlobRef.current = blob;
        if (stopIntentRef.current === "send") {
          void emitSend(blob, durationMs);
          return;
        }
        finalUrlRef.current = URL.createObjectURL(blob);
        setPhase("preview");
      };

      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyserRef.current = analyser;
      source.connect(analyser);

      const buffer = new Uint8Array(analyser.fftSize);

      startedAtRef.current = performance.now();
      setElapsedMs(0);
      setLiveSamples([]);
      liveSamplesRef.current = [];

      recorder.start();

      sampleIntervalRef.current = window.setInterval(() => {
        const a = analyserRef.current;
        if (!a) return;
        a.getByteTimeDomainData(buffer);
        let peak = 0;
        for (let i = 0; i < buffer.length; i++) {
          const v = Math.abs(buffer[i] - 128);
          if (v > peak) peak = v;
        }
        const normalized = Math.min(100, Math.round((peak / 128) * 100));
        liveSamplesRef.current.push(normalized);
        setLiveSamples((prev) => [...prev, normalized]);
      }, SAMPLE_INTERVAL_MS);

      timerIntervalRef.current = window.setInterval(() => {
        const elapsed = performance.now() - startedAtRef.current;
        setElapsedMs(elapsed);
        if (elapsed >= MAX_DURATION_MS) {
          stopRecording();
        }
      }, 100);
    } catch (err) {
      console.error("Failed to start recording:", err);
      toast.error("Microphone permission denied");
      onCancel();
    }
  };

  const ensurePreviewAudio = () => {
    if (previewAudioRef.current || !finalUrlRef.current) {
      return previewAudioRef.current;
    }
    const audio = new Audio(finalUrlRef.current);
    previewAudioRef.current = audio;
    audio.addEventListener("timeupdate", () => {
      if (audio.duration > 0) {
        setPreviewPosition(audio.currentTime / audio.duration);
      }
    });
    audio.addEventListener("ended", () => {
      voicePlayback.release(audio);
      setIsPlayingPreview(false);
      setPreviewPosition(0);
    });
    audio.addEventListener("pause", () => {
      if (!voicePlayback.isCurrent(audio)) setIsPlayingPreview(false);
    });
    return audio;
  };

  const togglePreviewPlayback = () => {
    const audio = ensurePreviewAudio();
    if (!audio) return;
    if (audio.paused) {
      voicePlayback.claim(audio);
      audio.play().catch(() => undefined);
      setIsPlayingPreview(true);
    } else {
      audio.pause();
      voicePlayback.release(audio);
      setIsPlayingPreview(false);
    }
  };

  const seekPreviewTo = (ratio: number) => {
    const audio = ensurePreviewAudio();
    if (!audio) return;
    const clamped = Math.min(1, Math.max(0, ratio));
    const totalDuration =
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : finalDurationRef.current / 1000;
    if (!totalDuration) return;
    const target = clamped * totalDuration;
    if (Number.isFinite(target)) {
      audio.currentTime = target;
      setPreviewPosition(clamped);
    }
  };

  const handleWaveformPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (phase !== "preview") return;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const ratio = ratioFromPointer(e.clientX, el.getBoundingClientRect());
    setDragProgress(ratio);
  };

  const handleWaveformPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragProgress === null) return;
    const ratio = ratioFromPointer(
      e.clientX,
      e.currentTarget.getBoundingClientRect(),
    );
    setDragProgress(ratio);
  };

  const handleWaveformPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
    if (dragProgress !== null) {
      seekPreviewTo(dragProgress);
      setDragProgress(null);
    }
  };

  const emitSend = async (blob: Blob, durationMs: number) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    const waveform = downsampleWaveform(finalSamplesRef.current, WAVEFORM_BARS);

    // Transcode to MP3 so the clip plays in every browser/OS. m4a (AAC) and
    // ogg/opus recordings fail on some platforms (e.g. Linux Firefox).
    setIsConverting(true);
    let outBlob = blob;
    let extension = "mp3";
    try {
      // Lazy-loaded so the MP3 encoder only ships to users who record audio.
      const { encodeToMp3 } = await import("./audioEncoder");
      outBlob = await encodeToMp3(blob);
    } catch (err) {
      console.error(
        "Voice message MP3 conversion failed, sending original recording:",
        err,
      );
      extension = mimeInfoRef.current.extension;
    }
    setIsConverting(false);

    const fileName = buildVoiceFileName(extension);
    const file = new File([outBlob], fileName, { type: outBlob.type });
    cleanupPreview();
    onSend(file, Math.round(durationMs), waveform);
  };

  const handleSend = () => {
    if (!finalBlobRef.current) return;
    void emitSend(finalBlobRef.current, finalDurationRef.current);
  };

  const stopAndSend = () => {
    stopIntentRef.current = "send";
    stopRecording();
  };

  useEffect(() => {
    voicePlayback.setRecording(true);
    void startRecording();
    return () => {
      voicePlayback.setRecording(false);
      clearIntervals();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.onstop = null;
          recorder.stop();
        } catch {
          // noop
        }
      }
      releaseMediaResources();
      cleanupPreview();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = waveformContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const compute = () => {
      const width = el.clientWidth;
      const slots = Math.max(1, Math.floor(width / 4));
      setBarSlots(slots);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const displaySamples =
    phase === "recording" ? liveSamples : finalSamplesRef.current;

  return (
    <div className="flex items-center gap-2 min-h-12 lg:min-h-14 px-3 lg:px-4 py-2 bg-white">
      <button
        type="button"
        onClick={handleCancel}
        disabled={disabled || isConverting}
        className="w-10 h-10 flex-shrink-0 rounded-full border border-gray-200 bg-gray-100 flex items-center justify-center text-red-500 disabled:opacity-50"
        aria-label="Cancel recording"
      >
        <Icon name="fasTrash" className="w-[14px] h-[14px]" />
      </button>

      <div className="flex-1 flex items-center gap-1.5 h-10 px-1 rounded-full bg-[#1E293B]">
        {phase === "recording" ? (
          <button
            type="button"
            onClick={stopRecording}
            disabled={disabled}
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center disabled:opacity-50"
            aria-label="Stop recording"
          >
            <Icon name="stop" className="w-5 h-5 text-red-500" />
          </button>
        ) : (
          <button
            type="button"
            onClick={togglePreviewPlayback}
            disabled={isConverting}
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-white disabled:opacity-50"
            aria-label={isPlayingPreview ? "Pause preview" : "Play preview"}
          >
            <Icon
              name={isPlayingPreview ? "pause" : "play"}
              className="w-4 h-4"
            />
          </button>
        )}

        <div
          ref={waveformContainerRef}
          className={`flex-1 min-w-0 flex items-center h-6 overflow-hidden ${
            phase === "preview" ? "cursor-pointer touch-none select-none" : ""
          }`}
          onPointerDown={handleWaveformPointerDown}
          onPointerMove={handleWaveformPointerMove}
          onPointerUp={handleWaveformPointerUp}
          onPointerCancel={handleWaveformPointerUp}
        >
          {phase === "recording" ? (
            <div className="flex items-center h-full w-full justify-end">
              {displaySamples.slice(-barSlots).map((amp, i) => {
                const height = Math.max(3, (amp / 100) * 21 + 3);
                return (
                  <div
                    key={i}
                    className="rounded-full bg-white"
                    style={{
                      height: `${height}px`,
                      marginRight: "1.5px",
                      width: "2.5px",
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <div className="flex items-center h-full w-full">
              {Array.from({ length: barSlots }).map((_, i) => {
                const sampleIdx =
                  displaySamples.length > 0
                    ? Math.floor((i / barSlots) * displaySamples.length)
                    : -1;
                const amp = sampleIdx >= 0 ? displaySamples[sampleIdx] : 0;
                const height = Math.max(3, (amp / 100) * 21 + 3);
                const displayProgress = dragProgress ?? previewPosition;
                const activeCount = Math.floor(displayProgress * barSlots);
                const isActive = i < activeCount;
                return (
                  <div
                    key={i}
                    className={`rounded-full ${
                      isActive ? "bg-white" : "bg-white/30"
                    }`}
                    style={{
                      height: `${height}px`,
                      marginRight: "1.5px",
                      width: "2.5px",
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        <span className="text-[13px] font-semibold tabular-nums text-white flex-shrink-0 pr-1">
          {formatVoiceTime(elapsedMs / 1000)}
        </span>

        {phase === "recording" && (
          <span
            className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0 mr-2"
            aria-hidden
          />
        )}
      </div>

      <button
        type="button"
        onClick={phase === "recording" ? stopAndSend : handleSend}
        disabled={disabled || isConverting}
        className="w-10 h-10 flex-shrink-0 rounded-full bg-[#1E293B] flex items-center justify-center text-white disabled:opacity-50"
        aria-label={
          isConverting
            ? "Preparing voice message"
            : phase === "recording"
              ? "Stop and send"
              : "Send voice message"
        }
      >
        {isConverting ? (
          <span
            className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin"
            aria-hidden
          />
        ) : (
          <Icon name="paperPlane" className="w-[14px] h-[14px]" />
        )}
      </button>
    </div>
  );
}
